import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import axios from "axios";

/**
 * Where the plate (the substrate) sits under the laser.
 *
 * Draws the laser's marking field and the plate on one canvas; the plate is
 * dragged with the pointer or typed as an offset (mm, laser x / y axes,
 * negative = the plate hangs out of the field). The slot grid, usable area
 * and header band come from the server (POST api/exploration/plate/layout),
 * which is the same computation the marking pipeline uses — nothing is
 * derived in the browser besides the picture.
 *
 * Props:
 *   materialId, laserDeviceId   : the wizard's selections
 *   plateDesignConfig           : the plate design (header flag etc.), or null for the generic one
 *   offset                      : { x, y } in mm
 *   onOffsetChange(offset)      : called while dragging and on typed changes
 *   onLayoutChange(layout|null) : the server's layout, null while it is invalid (e.g. plate outside the field)
 */
export const PREVIEW_URL = "api/exploration/plate/layout";
const CANVAS_WIDTH_PX = 560;
const MARGIN_MM = 12;

function roundMm(value) {
  return Math.round(value * 10) / 10;
}

function fmt(value) {
  return Number.isFinite(value) ? String(Math.round(value * 10) / 10) : "?";
}

export default function PlatePlacement({
  materialId,
  laserDeviceId,
  plateDesignConfig,
  offset,
  onOffsetChange,
  onLayoutChange,
}) {
  const [layout, setLayout] = useState(null);
  const [error, setError] = useState("");
  // Field and plate sizes survive an invalid position so the picture never
  // collapses while the plate is dragged out of the field.
  const [sizes, setSizes] = useState(null);
  const dragRef = useRef(null);
  const requestRef = useRef(0);

  const fetchLayout = useCallback(
    async (nextOffset) => {
      if (!materialId || !laserDeviceId) return;
      const id = ++requestRef.current;
      try {
        const res = await axios.post(PREVIEW_URL, {
          materialId,
          laserDeviceId,
          plateOffsetMm: { x: nextOffset.x, y: nextOffset.y },
          ...(plateDesignConfig ? { plateDesignConfig } : {}),
        });
        if (id !== requestRef.current) return;
        setLayout(res.data);
        setSizes({ field: res.data.field_mm, plate: res.data.plate_mm });
        setError("");
        if (onLayoutChange) onLayoutChange(res.data);
      } catch (err) {
        if (id !== requestRef.current) return;
        setLayout(null);
        setError(err?.response?.data?.error || err.message || "The plate position could not be computed.");
        if (onLayoutChange) onLayoutChange(null);
      }
    },
    [materialId, laserDeviceId, plateDesignConfig, onLayoutChange]
  );

  // A drag fires many offset changes a second; the server is asked once the
  // position rests for a moment (stale answers are dropped by request id).
  useEffect(() => {
    const timer = setTimeout(() => fetchLayout(offset), 120);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialId, laserDeviceId, plateDesignConfig, offset.x, offset.y]);

  // World box in mm: the field plus the plate wherever it is, with a margin.
  const world = useMemo(() => {
    const field = sizes?.field || { width: 100, height: 100 };
    const plate = sizes?.plate || { width: 100, height: 100 };
    const minX = Math.min(0, offset.x) - MARGIN_MM;
    const minY = Math.min(0, offset.y) - MARGIN_MM;
    const maxX = Math.max(field.width, offset.x + plate.width) + MARGIN_MM;
    const maxY = Math.max(field.height, offset.y + plate.height) + MARGIN_MM;
    return { minX, minY, width: maxX - minX, height: maxY - minY, field, plate };
  }, [sizes, offset.x, offset.y]);

  const scale = CANVAS_WIDTH_PX / world.width;
  const canvasHeight = world.height * scale;
  const px = (mm) => mm * scale;
  const toX = (mm) => px(mm - world.minX);
  const toY = (mm) => px(mm - world.minY);

  const onPointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = { startX: event.clientX, startY: event.clientY, origin: { ...offset } };
  };
  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const dx = (event.clientX - dragRef.current.startX) / scale;
    const dy = (event.clientY - dragRef.current.startY) / scale;
    onOffsetChange({ x: roundMm(dragRef.current.origin.x + dx), y: roundMm(dragRef.current.origin.y + dy) });
  };
  const onPointerUp = (event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const setAxis = (axis) => (event) => {
    const value = parseFloat(event.target.value);
    if (Number.isFinite(value)) onOffsetChange({ ...offset, [axis]: value });
  };
  const toCorner = () => onOffsetChange({ x: 0, y: 0 });
  const toCentre = () =>
    onOffsetChange({
      x: roundMm((world.field.width - world.plate.width) / 2),
      y: roundMm((world.field.height - world.plate.height) / 2),
    });

  const usable = layout?.usable_mm;
  const slot = layout?.slot_mm;

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <svg
          data-testid="plate-placement-canvas"
          width={CANVAS_WIDTH_PX}
          height={canvasHeight}
          viewBox={`0 0 ${CANVAS_WIDTH_PX} ${canvasHeight}`}
          style={{ display: "block", margin: "0 auto", touchAction: "none", userSelect: "none" }}
        >
          {/* marking field */}
          <rect
            x={toX(0)}
            y={toY(0)}
            width={px(world.field.width)}
            height={px(world.field.height)}
            fill="#f4f6f8"
            stroke="#5b6b7a"
            strokeWidth={1.5}
            strokeDasharray="6 4"
          />
          <text x={toX(0)} y={toY(world.field.height) + 14} fontSize={12} fill="#5b6b7a">
            {`Marking field ${fmt(world.field.width)} × ${fmt(world.field.height)} mm`}
          </text>
          {/* usable area + slots */}
          {usable ? (
            <rect
              x={toX(usable.x)}
              y={toY(usable.y)}
              width={px(usable.width)}
              height={px(usable.height)}
              fill="#dbeafe"
              stroke="none"
            />
          ) : null}
          {layout && layout.header_height_mm > 0 && usable ? (
            <rect
              x={toX(usable.x)}
              y={toY(usable.y)}
              width={px(usable.width)}
              height={px(layout.header_height_mm)}
              fill="#c7d2fe"
              stroke="none"
            />
          ) : null}
          {layout && slot
            ? layout.slots.map((s) => (
                <rect
                  key={`${s.row}-${s.col}`}
                  data-testid="plate-slot"
                  x={toX(s.x)}
                  y={toY(s.y)}
                  width={px(slot.width)}
                  height={px(slot.height)}
                  fill="#93a4b8"
                  stroke="#ffffff"
                  strokeWidth={0.5}
                />
              ))
            : null}
          {/* plate (draggable) */}
          <rect
            data-testid="plate-rect"
            x={toX(offset.x)}
            y={toY(offset.y)}
            width={px(world.plate.width)}
            height={px(world.plate.height)}
            fill="rgba(76, 126, 255, 0.12)"
            stroke={error ? "#d32f2f" : "#4C7EFF"}
            strokeWidth={2}
            style={{ cursor: "grab" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
          <text x={toX(offset.x) + 6} y={toY(offset.y) - 5} fontSize={12} fill="#4C7EFF">
            {`Plate ${fmt(world.plate.width)} × ${fmt(world.plate.height)} mm`}
          </text>
          {/* field origin marker */}
          <circle cx={toX(0)} cy={toY(0)} r={3} fill="#5b6b7a" />
        </svg>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          label="Offset x (mm)"
          type="number"
          size="small"
          inputProps={{ step: 0.5 }}
          value={offset.x}
          onChange={setAxis("x")}
          sx={{ width: 150 }}
        />
        <TextField
          label="Offset y (mm)"
          type="number"
          size="small"
          inputProps={{ step: 0.5 }}
          value={offset.y}
          onChange={setAxis("y")}
          sx={{ width: 150 }}
        />
        <Button variant="outlined" size="small" onClick={toCorner} sx={{ textTransform: "none" }}>
          Corner
        </Button>
        <Button variant="outlined" size="small" onClick={toCentre} sx={{ textTransform: "none" }}>
          Centre
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : layout ? (
        <Typography variant="body2" color="text.secondary" data-testid="plate-placement-summary">
          {`Usable ${fmt(usable.width)} × ${fmt(usable.height)} mm`
            + (layout.plate_margin_mm > 0 ? ` (${fmt(layout.plate_margin_mm)} mm edge margin)` : "")
            + ` • ${layout.grid.rows} × ${layout.grid.cols} slots • ${layout.grid.slots} per plate`}
        </Typography>
      ) : null}
    </Stack>
  );
}
